import { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';

import { cn } from '@/lib/utils';
import Input from '../Input';
import Button from '../Button';
import WorkWithUsModalLoader from './Loader';

import { IAboutUsDictionary } from '@/dictionaries/pages/aboutUs/types';
import { getTranslatedSchema } from './schema';
import { postCV } from '@/actions/contacts';

import './styles.css';

type Props = {
  dict: IAboutUsDictionary;
  closeModal: () => void;
  showSuccessModal: () => void;
};

export default function WorkWithUsModalContent({
  dict,
  closeModal,
  showSuccessModal,
}: Props) {
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [isLoading, setIsLoading] = useState(false);
  const schema = getTranslatedSchema(dict.workWithUs.modal.errors);
  const modalDict = dict.workWithUs.modal;

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const fileList = event.target.files;
    if (fileList) {
      const file = fileList[0];
      setValue('file', file);
      setUploadedFile(file);
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    reset();
    setIsLoading(true);

    const formData = new FormData();

    if (uploadedFile) {
      formData.append('name', data.name);
      formData.append('surname', data.surname);
      formData.append('email', data.email);
      formData.append('file', uploadedFile);
      formData.append('privacyPolicy', data.terms.toString());
      formData.append('type', 'workWithUs');

      postCV(formData).then(() => {
        closeModal();
        setIsLoading(false);
        showSuccessModal();
      });
    }
  });

  return (
    <>
      <div className="w-full bg-white py-4 px-5 relative">
        {isLoading && <WorkWithUsModalLoader />}
        <div className="p-4 pt-0">
          <div className="mb-8 text-center">
            <h3 className="text-medium mb-2 font-semibold">
              {modalDict.title}
            </h3>
            <p className="mb-4">{modalDict.description}</p>
          </div>
          <form onSubmit={onSubmit}>
            <div className="flex flex-wrap -mx-4">
              <Input.Container className="w-full desktop:w-[calc(50%-0.9375rem)] desktop:mr-[0.9375rem] mb-4">
                <Input.Label htmlFor="name"> {modalDict.name}</Input.Label>
                <Input.Root register={register} name="name" />
                <Input.Error error={errors.name} />
              </Input.Container>
              <Input.Container className="w-full desktop:w-[calc(50%-0.9375rem)] desktop:ml-[0.9375rem] mb-4">
                <Input.Label htmlFor="surname">
                  {' '}
                  {modalDict.surname}
                </Input.Label>
                <Input.Root register={register} name="surname" />
                <Input.Error error={errors.surname} />
              </Input.Container>
              <Input.Container className="w-full desktop:w-[calc(50%-0.9375rem)] desktop:mr-[0.9375rem]  mb-4">
                <Input.Label htmlFor="email"> {modalDict.email}</Input.Label>
                <Input.Root register={register} name="email" />
                <Input.Error error={errors.email} />
              </Input.Container>
              <Input.Container className="w-full desktop:w-[calc(50%-0.9375rem)] desktop:ml-[0.9375rem] mb-4">
                <Input.Label htmlFor="upload">
                  {modalDict.uploadYourCv}
                </Input.Label>
                <input
                  onChange={handleFileChange}
                  id="file-input"
                  type="file"
                  className="hidden"
                />
                <div className="w-full flex">
                  <Input.Root
                    className="overflow-ellipsis"
                    value={uploadedFile?.name}
                    onChange={() => undefined}
                    name="upload"
                    disabled
                  />
                  <label
                    htmlFor="file-input"
                    className="relative bg-white text-base w-12 h-full text-blue border border-blue flex items-center justify-center">
                    <span className="file-select-button-icon"></span>
                  </label>
                </div>
                {errors.file?.message && (
                  <i className="text-error-primary font-thin text-xs mt-2">
                    {errors.file?.message}
                  </i>
                )}
              </Input.Container>
              <div className="flex flex-col desktop:w-full desktop:mb-4">
                <div className="flex items-start gap-2 md:w-80 lg:w-[30rem] xl:w-full">
                  <input
                    className={cn('checkbox-input', {
                      'border border-underline': watch('terms') == true,
                    })}
                    {...register('terms')}
                    onChange={(e) => setValue('terms', e.target.checked)}
                    type="checkbox"
                    name="checkbox-input"
                  />
                  <label
                    className="text-sm text-footer-gray"
                    htmlFor="checkbox-input">
                    {modalDict.terms}
                    <Link
                      href={modalDict.privacyHref}
                      target="_blank"
                      className="hover:text-gray-700 underline cursor-pointer ml-1 text-footer-gray">
                      {modalDict.privacyPolicy}
                    </Link>
                  </label>
                </div>
                <Input.Error error={errors.terms} />
              </div>
              <Button
                type="submit"
                className="mt-4 desktop:w-[calc(50%-1.2rem)] desktop:ml-auto">
                {modalDict.sendYouRequest}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
